import React, { useEffect, useRef } from 'react';
import NotificationItem, {
  NotificationItemProps,
} from '@components/navigation/notification/NotificationItem';
import ArrowLeft from '@assets/icons/arrow-left';
import { LoadingScroll } from 'geniebook-ui/dist';
import EmptyNotification from '@components/navigation/notification/EmptyNotification';
import NotificationError from '@components/navigation/notification/NotificationError';
import { useLocation, useParams } from 'react-router-dom';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/subjectIdFromName';

export interface NotificationContainerProps {
  notificationItems: NotificationItemProps[];
  onClose: () => void;
  loading: boolean;
  loadingNext: boolean;
  onNextFetch: () => void;
  isError?: boolean;
}

const NotificationContainer = (props: NotificationContainerProps) => {
  const { subjectId: subjectName } = useParams();
  const { pathname } = useLocation();

  // detects current page to properly handle JC user identification in both Profile (general page) and Dashboard (a page with a subject in the URL)
  const isGeneralPage = () => {
    const firstSegment = pathname.split('/').filter(Boolean)[0];
    return subjectIdFromName(firstSegment) === 0;
  };

  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(subjectName ?? ''),
    generalPage: isGeneralPage(),
  });
  const notificationRef = useRef<HTMLDivElement>(null);
  const lastNotificationRef = useRef<HTMLDivElement>(null);

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        props.onNextFetch();
      }
    },
    {
      root: notificationRef.current,
      threshold: 0.1,
    },
  );

  useEffect(() => {
    if (lastNotificationRef.current) {
      observer.observe(lastNotificationRef.current);
    }
  }, [props.notificationItems.length]);

  function handleClose(event: React.MouseEvent) {
    event.stopPropagation();
    props.onClose();
  }

  return (
    <div className={'md:w-[368px] max-h-[calc(100vh-116px)] md:h-[550px]'}>
      <div
        className={
          'md:hidden md:text-primary-navyblue font-black mb-4 pr-5 max-md:text-md max-md:relative max-md:flex max-md:w-screen max-md:p-3 max-md:items-center max-md:!mb-0 max-md:text-neutral-whiteboard max-md:bg-primary-navyblue'
        }
      >
        <ArrowLeft
          onClick={event => handleClose(event)}
          className={
            'w-4 mr-2 fill-current text-primary-apricot max-md:inline-block max-md:fill-primary-apricot'
          }
          data-testid={'close-mobile'}
        />
        Notifications
      </div>
      {props.loading ? (
        <div className={'w-full relative md:h-[70vh] max-md:h-[90vh] pr-5'}>
          <LoadingScroll
            className={
              'top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 absolute'
            }
          />
        </div>
      ) : props.isError ? (
        <div
          className={`pb-6 pr-5 ${
            isJC === true ? 'flex justify-center items-center min-h-full' : ''
          }`}
        >
          <NotificationError isJC={isJC} />
        </div>
      ) : (
        <>
          {props.notificationItems.length > 0 ? (
            <div
              className={
                'md:max-h-full max-md:max-h-[calc(100%-48px)] max-md:p-4 overflow-auto no-scrollbar'
              }
              id={'notification-container'}
              data-testid={'notification-container'}
              ref={notificationRef}
            >
              {props.notificationItems.map((item, index) => (
                <div className={'mb-2'} key={index}>
                  <NotificationItem {...item} />
                </div>
              ))}
              {props.loadingNext ? (
                <div className={'w-full relative h-12 py-3 pr-5'}>
                  <LoadingScroll
                    className={
                      'top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 absolute w-20'
                    }
                  />
                </div>
              ) : null}
              <div className={'h-8'} ref={lastNotificationRef}></div>
            </div>
          ) : (
            <div
              className={`${
                isJC === true
                  ? 'flex justify-center items-center min-h-full'
                  : ''
              } pb-6 pr-5`}
              data-testid={'empty-notification-container'}
            >
              <EmptyNotification isJC={isJC} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationContainer;
